import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCog, UserPlus, Building2, Lock, Linkedin, ShieldCheck, CreditCard, Trash2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NewWorkspaceDialog } from "@/components/NewWorkspaceDialog";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ROLE_COLOR = { owner: "text-amber-500 bg-amber-500/10", admin: "text-sky-500 bg-sky-500/10", sdr: "text-emerald-500 bg-emerald-500/10" };

export default function Team() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [org, setOrg] = useState(null);
  const [seats, setSeats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "sdr" });
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    const [t, w, o, s] = await Promise.all([api.get("/team"), api.get("/workspaces"), api.get("/org/status"), api.get("/team/seats")]);
    setMembers(t.data); setWorkspaces(w.data); setOrg(o.data); setSeats(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const isEnterprise = org?.plan_id === "enterprise";
  const atCap = seats?.at_cap;

  const invite = async () => {
    if (!form.email || !form.name) return toast.error("Name and email required");
    try {
      await api.post("/team/invite", form);
      setOpen(false); setForm({ name: "", email: "", role: "sdr" }); load();
      toast.success(`Invited ${form.email} as ${form.role}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code === "seat_limit") toast.error(d.message, { description: "Buy a seat below, then invite again." });
      else toast.error("Could not send invite");
    }
  };

  const buySeat = () => { if (seats?.extra_seat_lookup && seats?.seat_billing_live) startCheckout(seats.extra_seat_lookup); };

  const myRole = members.find((m) => m.user_id === user?.id)?.role;
  const canRemove = (m) => {
    if (m.user_id === user?.id) return false;
    if (myRole === "owner") return true;
    if (myRole === "admin") return m.role === "sdr";
    return false;
  };
  const doRemove = async () => {
    try {
      await api.delete(`/team/members/${removing.user_id}`);
      toast.success(`Removed ${removing.name}. Seat freed.`);
      setRemoving(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not remove member");
      setRemoving(null);
    }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="Team Settings" title="Roles, seats & workspaces"
        subtitle="Owner, Admin, SDR roles. Organization owns billing; workspaces isolate GTM motions."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="invite-btn" disabled={atCap} title={atCap ? "At seat cap - buy a seat first" : ""}><UserPlus className="h-4 w-4 mr-1.5" /> Invite</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite a teammate</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="invite-name" /></div>
                <div><Label>Work email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="invite-email" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="owner">Owner</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="sdr">SDR</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={invite} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid="send-invite-btn">Send invite</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {seats && (
        <div className="rounded-xl border border-border/70 bg-card/50 p-5 mb-6" data-testid="seat-summary">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-amber-500" />
              <div>
                <div className="text-sm font-medium">
                  Seats: <span className="metric" data-testid="seat-used">{seats.used}</span>
                  <span className="text-muted-foreground"> / {seats.unlimited ? "unlimited" : seats.cap}</span>
                  <span className="text-xs text-muted-foreground ml-2">{seats.plan_name} · {seats.seats_included ?? "custom"} included{seats.extra_seats ? ` + ${seats.extra_seats} extra` : ""}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {seats.unlimited ? "Enterprise: add users without a seat cap."
                    : atCap ? "You're at your seat cap. Buy a seat to invite another user."
                    : `${seats.cap - seats.used} seat${seats.cap - seats.used === 1 ? "" : "s"} available.`}
                </div>
              </div>
            </div>
            {!seats.unlimited && (
              seats.can_add_seats ? (
                <div className="flex flex-col items-end gap-1">
                  <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[40px]"
                    onClick={buySeat} disabled={!seats.seat_billing_live} data-testid="buy-seat-btn">
                    <CreditCard className="h-4 w-4 mr-1.5" /> Buy a seat · ${seats.extra_seat_price}/mo
                  </Button>
                  {!seats.seat_billing_live && <span className="text-[11px] text-amber-500" data-testid="billing-not-connected">Billing not connected</span>}
                </div>
              ) : (
                <div className="flex flex-col items-end gap-1">
                  <Button size="sm" variant="outline" className="rounded-md min-h-[40px]" onClick={() => navigate("/app/admin/billing")} data-testid="upgrade-for-seats-btn">
                    <Lock className="h-3.5 w-3.5 mr-1.5" /> Upgrade to add seats
                  </Button>
                  <span className="text-[11px] text-muted-foreground">{seats.plan_name} has a fixed seat count</span>
                </div>
              )
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <div className="flex items-center gap-2 mb-4"><UserCog className="h-4 w-4 text-amber-500" /><h3 className="font-display text-lg font-semibold tracking-tight">Members</h3></div>
          <div className="space-y-2" data-testid="team-members">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between rounded-lg border border-border/60 p-3 group" data-testid={`member-row-${m.user_id}`}>
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center text-xs font-semibold">{m.name?.slice(0, 1)}</span>
                  <div><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-muted-foreground font-mono">{m.email}</div></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${ROLE_COLOR[m.role] || ROLE_COLOR.sdr}`}>{m.role}</span>
                  {canRemove(m) && (
                    <button onClick={() => setRemoving(m)} data-testid={`remove-member-${m.user_id}`}
                      className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors" title="Remove member">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-500" /><h3 className="font-display text-lg font-semibold tracking-tight">GTM Workspaces</h3></div>
            <Button size="sm" variant="outline" className="rounded-md" data-testid="new-ws-btn" onClick={() => setNewWsOpen(true)}>New</Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Isolated GTM contexts under {" "}<span className="text-foreground">the same organization</span>. Each starts empty with its own offers, leads, campaigns and pipeline.</p>
          <div className="space-y-2" data-testid="team-workspaces">
            {workspaces.map((w) => (
              <div key={w.id} className="rounded-lg border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{w.name}</span>
                  <Badge variant="outline" className="text-xs capitalize">{w.sector === "public" ? "Public sector" : "Private sector"}</Badge>
                </div>
                {w.description && <p className="text-xs text-muted-foreground mt-1">{w.description}</p>}
              </div>
            ))}
          </div>
          <NewWorkspaceDialog open={newWsOpen} onOpenChange={setNewWsOpen} onCreated={load} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {org?.feature_flags?.navigator_import !== false && (
        <div className="rounded-xl border border-border/70 bg-card/50 p-6" data-testid="nav-ingest-card">
          <div className="flex items-center gap-2 mb-3"><Linkedin className="h-4 w-4 text-sky-500" /><h3 className="font-display text-lg font-semibold tracking-tight">Sales Navigator ingest</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Bring your own Navigator license. DealsOps never scrapes LinkedIn or sends DMs. Import a Navigator CSV - rows without a work email become unverified and go to the LinkedIn task queue for manual outreach. Navigator is not a licensed seat included in your plan.</p>
          <Button size="sm" variant="outline" className="rounded-md" data-testid="team-nav-import-btn" onClick={() => navigate("/app/leads")}>Import Navigator CSV in Lead Database</Button>
        </div>
        )}

        <div className={`rounded-xl border p-6 ${isEnterprise ? "border-border/70" : "border-border/60 opacity-70"} bg-card/50`} data-testid="sso-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /><h3 className="font-display text-lg font-semibold tracking-tight">SSO - SAML</h3></div>
            <Switch checked={false} disabled={!isEnterprise} data-testid="sso-toggle" />
          </div>
          <p className="text-xs text-muted-foreground mb-3">{isEnterprise ? "Available on your plan. SAML identity-provider setup is handled with your success manager." : "Available on Enterprise. Single sign-on with your SAML identity provider."}</p>
          {!isEnterprise && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" /> Enterprise only</Badge>
              <Button size="sm" variant="ghost" className="text-amber-500" data-testid="sso-upgrade-btn" onClick={() => navigate("/app/admin/billing")}>Talk to us about Enterprise</Button>
            </div>
          )}
        </div>
      </div>
      <Dialog open={!!removing} onOpenChange={(v) => !v && setRemoving(null)}>
        <DialogContent data-testid="remove-member-dialog">
          <DialogHeader><DialogTitle>Remove teammate?</DialogTitle>
            <DialogDescription>
              Remove <span className="text-foreground font-medium">{removing?.name}</span> ({removing?.role}) from this organization? This frees a seat immediately. It does not refund Stripe.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setRemoving(null)} data-testid="cancel-remove">Cancel</Button>
            <Button className="rounded-md bg-rose-500 text-white hover:bg-rose-600" onClick={doRemove} data-testid="confirm-remove"><Trash2 className="h-4 w-4 mr-1.5" /> Remove member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageBody>
  );
}
